/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */
import { Backoff } from '@openphone/internal-api-client'
import axios from 'axios'
import Debug from 'debug'
import throttle from 'lodash/throttle'
import { action, makeObservable, observable, reaction } from 'mobx'

import { logError } from '@src/lib/log'
import type PresenceService from '@src/service/presence/PresenceService'

import type WebSocket from './websocket'

import { getRootLogger } from '@odyssey/logging/logger'

export default class Connectivity {
  online = true

  private lastHeartbeat = Date.now()
  private backoff: Backoff
  private debug = Debug('op:transport:connectivity')
  private request = axios.create({
    timeout: 20000,
  })

  private get log() {
    return getRootLogger().getChild(['network', 'connectivity'])
  }

  get downtime() {
    return this.websocket.downtime
  }

  constructor(
    private presence: PresenceService,
    private websocket: WebSocket,
  ) {
    this.backoff = Backoff.exponential({
      factor: 2.0,
      initialDelay: 100,
      maxDelay: 20000,
      randomisationFactor: 0.4,
    })

    // If network is detected, check for internet connection
    if (window.navigator.onLine) {
      this.checkInternetConnection()
    }

    makeObservable(this, {
      online: observable,
    })

    reaction(
      () => this.websocket.isOpen,
      () => this.checkInternetConnection(),
    )

    reaction(
      () => this.online,
      (online) => this.debug(online ? 'online!' : 'offline!'),
    )

    setInterval(() => {
      this.lastHeartbeat = Date.now()
    }, 60_000)

    reaction(
      () => this.presence.visibilityState,
      (visibilityState) => {
        const now = Date.now()
        if (visibilityState === 'visible' && this.lastHeartbeat < now - 90_000) {
          this.log.debug(
            `[NETWORK] Old heartbeat detected (${
              now - this.lastHeartbeat
            }ms ago), marking as offline and checking connection`,
          )
          this.online = false
          this.lastHeartbeat = now
          this.checkInternetConnection()
        }
      },
    )

    // Called when a backoff timer is started.
    this.backoff.on(
      'backoff',
      action((_: any, delay: number) => {
        this.debug('connection could not be established, trying in %dms.', delay)
        this.online = false
      }),
    )

    // Called when a backoff timer ends. We want to try to reconnect
    // the WebSocket at this point.
    this.backoff.on('ready', (_attempt: number) => {
      this.checkInternetConnection()
    })

    window.addEventListener('online', () => {
      this.backoff.reset()
      this.checkInternetConnection()
    })

    window.addEventListener(
      'offline',
      action(() => {
        this.online = false
      }),
    )
  }

  private checkInternetConnection = throttle(() => {
    this.debug('checking internet connection')
    this.request
      .get('https://ping.openphoneapi.com')
      .then(
        action(() => {
          this.online = true
          this.backoff.reset()
        }),
      )
      .catch(
        action(() => {
          this.online = false
          try {
            this.backoff.backoff()
          } catch (error) {
            logError(error)
          }
        }),
      )
  }, 2_000)
}
